import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../../../../lib/network/api";
import { JobApplicationPayload } from "../types/careers";
import { ApiError, ApiResponse } from "../../../../lib/network/axios";

// ==================== INTERFACES ====================

export interface ParsedResumeData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  location?: string;
  experience?: string;
  skills?: string[];
  summary?: string;
}

export interface GeoLocationData {
  ip: string;
  city: string;
  region: string;
  country: string;
  countryCode: string;
  timezone: string;
  latitude: number;
  longitude: number;
}

// ==================== API FUNCTIONS ====================

export const submitJobApplication = async (
  payload: JobApplicationPayload
): Promise<ApiResponse> => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (key === "resume" && value instanceof File) {
      formData.append("resume", value);
    } else if (value !== null && value !== undefined && key !== "resume") {
      formData.append(key, value as string);
    }
  });

  const res = await api.post<ApiResponse>("/applications", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return res;
};

export const parseResume = async (file: File): Promise<ParsedResumeData> => {
  const formData = new FormData();
  formData.append("resume", file);

  const res = await api.post<ApiResponse<ParsedResumeData>>(
    "/applications/parse-resume",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return res.data;
};

export const getGeoLocation = async (): Promise<GeoLocationData> => {
  // Using ip-api.com (free, no API key required, 45 requests/minute limit)
  const response = await fetch(
    "http://ip-api.com/json/?fields=status,message,country,countryCode,region,regionName,city,timezone,lat,lon,query"
  );
  const data = await response.json();

  if (data.status === "fail") {
    throw new Error(data.message || "Failed to get location");
  }

  return {
    ip: data.query,
    city: data.city,
    region: data.regionName,
    country: data.country,
    countryCode: data.countryCode,
    timezone: data.timezone,
    latitude: data.lat,
    longitude: data.lon,
  };
};

// Alternative: Using ipapi.co (free tier: 1000 requests/day)
export const getGeoLocationAlt = async (): Promise<GeoLocationData> => {
  const response = await fetch("https://ipapi.co/json/");
  const data = await response.json();

  if (data.error) {
    throw new Error(data.reason || "Failed to get location");
  }

  return {
    ip: data.ip,
    city: data.city,
    region: data.region,
    country: data.country_name,
    countryCode: data.country_code,
    timezone: data.timezone,
    latitude: data.latitude,
    longitude: data.longitude,
  };
};

// ==================== HOOKS ====================

export const useSubmitJobApplication = () => {
  return useMutation<ApiResponse, ApiError, JobApplicationPayload>({
    mutationFn: submitJobApplication,
    onSuccess: (response) => {
      toast.success("Application Submitted", {
        description: response.message || "Your application has been received.",
      });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Something went wrong. Please try again.";

      toast.error("Application Failed", {
        description: errorMessage,
      });
    },
  });
};

export const useParseResume = () => {
  return useMutation<ParsedResumeData, ApiError, File>({
    mutationFn: parseResume,
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.message || "Failed to parse resume";
      toast.error("Resume Parse Failed", {
        description: errorMessage,
      });
    },
  });
};

export const useGeoLocation = () => {
  return useQuery({
    queryKey: ["geo-location"],
    queryFn: async () => {
      // Try primary API first, fallback to alternative
      try {
        return await getGeoLocationAlt(); // ipapi.co works over HTTPS
      } catch {
        // If HTTPS fails, try the HTTP endpoint (ip-api.com free tier is HTTP only)
        try {
          return await getGeoLocation();
        } catch {
          return null;
        }
      }
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    retry: 1,
    refetchOnWindowFocus: false,
  });
};
